import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { formatDate, getLeaveTypeName, getStatusBadge } from '../lib/utils';

export const Leave = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'sick_leave',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/leave/requests');
      setRequests(response.data.data.requests);
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/leave/requests', formData);
      toast.success('Leave request submitted successfully');
      setDialogOpen(false);
      setFormData({ leave_type: 'sick_leave', start_date: '', end_date: '', reason: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading" data-testid="leave-title">My Leaves</h1>
          <p className="text-slate-600 mt-1" data-testid="leave-subtitle">View and manage your leave requests</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" data-testid="new-leave-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Leave Request
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="leave-dialog">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
              <DialogDescription>Fill in the details to submit your leave request</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="leave-form">
              <div className="space-y-2">
                <Label htmlFor="leave_type">Leave Type</Label>
                <Select value={formData.leave_type} onValueChange={(value) => setFormData({ ...formData, leave_type: value })}>
                  <SelectTrigger data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sick_leave">Sick Leave</SelectItem>
                    <SelectItem value="casual_leave">Casual Leave</SelectItem>
                    <SelectItem value="vacation">Vacation</SelectItem>
                    <SelectItem value="maternity_leave">Maternity Leave</SelectItem>
                    <SelectItem value="paternity_leave">Paternity Leave</SelectItem>
                    <SelectItem value="unpaid_leave">Unpaid Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    data-testid="leave-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    data-testid="leave-end-date"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason</Label>
                <Textarea
                  id="reason"
                  placeholder="Briefly describe the reason for your leave"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                  rows={3}
                  data-testid="leave-reason"
                />
              </div>
              <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" disabled={submitting} data-testid="leave-submit-btn">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12" data-testid="leave-loading">
          <p className="text-slate-600">Loading...</p>
        </div>
      ) : requests.length === 0 ? (
        <Card data-testid="leave-empty">
          <CardContent className="py-12 text-center">
            <p className="text-slate-600">No leave requests found. Create your first request!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="leave-list">
          {requests.map((request) => (
            <Card key={request.request_id} className="leave-request-card" data-testid={`leave-request-${request.request_id}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900" data-testid="leave-request-type">
                        {getLeaveTypeName(request.leave_type)}
                      </h3>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadge(request.status)}`} data-testid="leave-request-status">
                        {request.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p data-testid="leave-request-dates">
                        {formatDate(request.start_date)} - {formatDate(request.end_date)} ({request.total_days} days)
                      </p>
                      <p data-testid="leave-request-reason"><strong>Reason:</strong> {request.reason}</p>
                      <p className="text-xs text-slate-500" data-testid="leave-request-created">Applied on {formatDate(request.created_at)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Leave;
